import { useState } from "react";
import { Lock, Eye, EyeOff, Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import { useAuth } from "../hooks/useAuth";

// Reached via the recovery link in the password-reset email. supabase-js parses
// the recovery token from the URL fragment on load and establishes a temporary
// session (onAuthStateChange fires PASSWORD_RECOVERY), so useAuth() exposes it
// as a normal signed-in session — we just collect the new password.
export default function ResetPasswordView() {
  const { user, loading, updatePassword } = useAuth();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const inputClass =
    "w-full bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-4 py-3 text-[15px] text-rc-black placeholder:text-rc-slate/70 focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15 transition-colors ease-rc-out";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) { setError("Das Passwort muss mindestens 6 Zeichen lang sein."); return; }
    setError(null);
    setSubmitting(true);
    try {
      const err = await updatePassword(password);
      if (err) setError(err.message);
      else setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Shell>
        <div className="w-full max-w-sm text-center animate-fade-up">
          <div className="w-12 h-12 rounded-full bg-rc-lime/20 border border-rc-lime/40 flex items-center justify-center mx-auto mb-7">
            <CheckCircle2 className="w-5 h-5 text-rc-black" />
          </div>
          <h1 className="text-[28px] font-bold text-rc-black tracking-[-0.02em] leading-[1.15] mb-3">
            Passwort geändert.
          </h1>
          <p className="text-rc-charcoal text-[15px] leading-[1.6]">
            Du kannst dich jetzt mit deinem neuen Passwort anmelden.
          </p>
          <a
            href="/facilitator"
            className="mt-7 inline-flex items-center justify-center gap-2 bg-rc-berry hover:bg-rc-berry-700 text-rc-paper rounded-pill px-6 py-3 text-[15px] font-semibold active:scale-[0.98] transition ease-rc-out"
          >
            Zur Konsole
          </a>
        </div>
      </Shell>
    );
  }

  // No recovery session → the link was invalid, already used, or expired.
  if (!loading && !user) {
    return (
      <Shell>
        <div className="w-full max-w-sm text-center animate-fade-up">
          <div className="w-12 h-12 rounded-full bg-rc-danger/10 border border-rc-danger/30 flex items-center justify-center mx-auto mb-7">
            <ShieldAlert className="w-5 h-5 text-rc-danger" />
          </div>
          <h1 className="text-[28px] font-bold text-rc-black tracking-[-0.02em] leading-[1.15] mb-3">
            Link ungültig.
          </h1>
          <p className="text-rc-charcoal text-[15px] leading-[1.6]">
            Dieser Link zum Zurücksetzen ist abgelaufen oder wurde bereits verwendet. Fordere über die Anmeldung einen neuen an.
          </p>
          <a
            href="/facilitator"
            className="mt-7 inline-flex items-center justify-center text-rc-berry hover:text-rc-berry-700 text-[14px] font-semibold transition-colors ease-rc-out"
          >
            Zurück zur Anmeldung
          </a>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="w-full max-w-sm animate-fade-up">
        <div className="flex flex-col items-center mb-8">
          <img src="/brand/readycal-logo.png" alt="READYCAL" className="h-5 w-auto block mb-7 opacity-90" />
          <h1 className="text-[28px] font-bold text-rc-black tracking-[-0.02em] leading-[1.1] text-center">
            Neues Passwort.
          </h1>
          <p className="text-rc-charcoal text-[15px] mt-2.5 text-center max-w-[22rem] leading-[1.55]">
            Wähle ein neues Passwort für dein <strong className="font-semibold text-rc-black">REASONZ-Konto</strong>.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-rc-paper border border-rc-fog rounded-rc-lg p-7 shadow-cardLg flex flex-col gap-3.5"
        >
          {error && (
            <div className="bg-rc-danger/8 border border-rc-danger/30 rounded-xl px-4 py-3 text-rc-danger text-[14px] font-medium">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="new-password" className="text-[11px] font-semibold uppercase tracking-[0.14em] text-rc-charcoal block mb-2">
              Neues Passwort
            </label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-rc-slate pointer-events-none" />
              <input
                id="new-password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                className={`${inputClass} pl-10 pr-11`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-rc-slate hover:text-rc-black rounded-full transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="mt-2 inline-flex items-center justify-center gap-2 bg-rc-berry hover:bg-rc-berry-700 disabled:bg-rc-fog disabled:text-rc-slate disabled:cursor-not-allowed text-rc-paper rounded-pill px-6 py-3.5 text-[15px] font-semibold active:scale-[0.98] transition ease-rc-out"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Passwort speichern
          </button>
        </form>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-rc-cream text-rc-black flex flex-col items-center justify-center px-6 py-16 font-sans antialiased">
      {children}
    </div>
  );
}
